import React, { useState } from 'react';
import { Card, Empty, Select, Space, Typography, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Api from '../../Network';
import SalesAnalyticsTab from '../SalesRepresentatives/SalesAnalyticsTab';
import RelatedCustomersTab from '../SalesRepresentatives/RelatedCustomersTab';
import { usePermissions } from '../../contexts/PermissionContext';
import { SALES_ANALYTICS_PERMISSIONS } from '../../constants/reportPermissions';

const { Text, Title } = Typography;

interface DriverOption {
  id: number;
  name: string;
}

const SalesAnalyticsReport: React.FC = () => {
  const { t } = useTranslation();
  const { canAccessMenuRoute } = usePermissions();
  const canViewReport = canAccessMenuRoute('/reports/sales-analytics', [
    SALES_ANALYTICS_PERMISSIONS.VIEW,
  ]);
  const canDownloadReport = canAccessMenuRoute('/reports/sales-analytics', [
    SALES_ANALYTICS_PERMISSIONS.DOWNLOAD,
  ]);

  const [analyticsTab, setAnalyticsTab] = useState<'products' | 'relatedCustomers'>('products');
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>(undefined);

  const { data: driversData, isLoading: driversLoading } = useQuery({
    queryKey: ['reports-drivers-list'],
    queryFn: async (): Promise<DriverOption[]> => {
      const response: any = await Api.get('/api/admin/drivers', {
        headers: { 'per-page': '1000' },
      });
      const rows = response?.data?.data || [];
      return rows.map((d: any) => ({ id: d.id, name: d.name }));
    },
    enabled: canViewReport || canDownloadReport,
  });

  const selectedDriverName = (driversData || []).find(
    (d) => String(d.id) === selectedDriverId
  )?.name;

  return (
    <div className="reports-page">
      <div className="reports-header">
        <Title level={3}>{t('reports.tabs.analytics', 'Sales analytics')}</Title>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap direction="vertical" style={{ width: '100%' }}>
          <Space wrap>
            <Text strong>
              {t('reports.analytics.selectRepresentative', 'Select representative')}
            </Text>
            <Select
              style={{ minWidth: 280 }}
              loading={driversLoading}
              value={selectedDriverId}
              onChange={(v) => setSelectedDriverId(String(v))}
              options={(driversData || []).map((d) => ({ value: String(d.id), label: d.name }))}
              showSearch
              optionFilterProp="label"
              placeholder={t(
                'reports.analytics.selectRepresentativePlaceholder',
                'Choose representative'
              )}
            />
          </Space>
          {!canViewReport && canDownloadReport && (
            <Text type="secondary">{t('reports.downloadOnlyHint')}</Text>
          )}
        </Space>
      </Card>

      {selectedDriverId ? (
        canViewReport ? (
          <Tabs
            activeKey={analyticsTab}
            onChange={(k) => setAnalyticsTab(k as 'products' | 'relatedCustomers')}
            items={[
              {
                key: 'products',
                label: t('salesRepresentatives.tabs.salesAnalytics', 'Sales analytics'),
                children: (
                  <SalesAnalyticsTab
                    enabled={analyticsTab === 'products'}
                    driverId={selectedDriverId}
                    driverName={selectedDriverName}
                    permissionContext="sales_analytics"
                    canViewContent
                    canDownload={canDownloadReport}
                  />
                ),
              },
              {
                key: 'relatedCustomers',
                label: t('salesRepresentatives.tabs.relatedCustomers', 'Related customers'),
                children: (
                  <RelatedCustomersTab
                    enabled={analyticsTab === 'relatedCustomers'}
                    driverId={selectedDriverId}
                  />
                ),
              },
            ]}
          />
        ) : (
          <SalesAnalyticsTab
            enabled
            driverId={selectedDriverId}
            driverName={selectedDriverName}
            permissionContext="sales_analytics"
            canViewContent={false}
            canDownload={canDownloadReport}
          />
        )
      ) : (
        <Card>
          <Empty
            description={t(
              'reports.analytics.selectRepresentativeFirst',
              'Select a representative to view analytics'
            )}
          />
        </Card>
      )}
    </div>
  );
};

export default SalesAnalyticsReport;
